import React, { useState, useEffect, useContext, useMemo } from 'react'
import {
    Typography,
    Button,
    Snackbar,
    Alert,
    Divider,
    IconButton,
    Dialog,
    DialogActions,
    DialogTitle,
    DialogContent,
    Stack,
    TextareaAutosize,
    TextField,
    Popover
} from '@mui/material'
import {
    PeopleAlt,
    Circle,
    CalendarToday,
    DeleteOutline,
    AutorenewOutlined,
    VpnKeyRounded,
    ChatBubbleOutline,
    CompressOutlined,
    ExpandOutlined,
    AddOutlined,
    AddCommentOutlined,
    KeyboardArrowLeft,
    KeyboardArrowRight,
    PersonAddAlt1,
} from '@mui/icons-material'
import LoadingButton from '@mui/lab/LoadingButton'
import { ERROR_COLOR, ROLE_MAPPINGS, CONFIRMED, BLOCKED, FUSOKU, PALO_BLUE, ENABLE_CONFIRMED_SHIFT_CHANGE_REASON, ENABLE_AUTOSCHEDULE_DAY_QUALITY } from '../../constants'
import './checkShift.css'
import ButtonWithConfirmPrompt from '../ButtonWithConfirmPrompt'
import { useParams, useLocation } from 'react-router-dom'
import { AxiosWithAuth } from '../../Utilities/authenticationService'
import {
    InputTime,
    MAX_LEGAL_SHIFT_DURATION_HOURS,
    parseTimeBlock,
    SHIFT_CALENDAR_SLOT_HOURS,
    splitShiftsIntoBlocks,
    stitchShifts,
    timezoneOffsets,
    validateShiftModalTimes
} from '../../Utilities/shiftUtils'
import { EmploymentsContext } from '../../contexts/EmploymentsContext'
import ShiftChangeReasonSelect from '../MonthlyShift/ShiftChangeReasonSelect'
import AutoscheduleDayQuality from './AutoscheduleDayQuality'
import moment from 'moment'
import PdfBulkExportButton from '../ShiftPdfExport/PdfBulkExportButton'
import DesktopCalendar from './Calendars/DesktopCalendar'
import MobileCalendar from './Calendars/MobileCalendar'
import SubmitShift from '../SubmitShift/SubmitShift'
import { Box } from '@mui/system'
import { isMobile } from '../../Utilities/isMobile'
import { MEMO_MAX_ROWS } from '../../constants'
import { displayNameWithSpace } from '../../Utilities/japaneseStringUtils'
import EditDailyNoteTemplateDrawer from './EditDailyNoteTemplateDrawer'
import useOrientation from '../../Utilities/useOrientation'
import SupporterDrawer from './SupporterDrawer'
export const getStaffReq = (staffRequirements = [], operationHours, shiftDate) => {
    const filteredStaffReqs = staffRequirements.filter(req => {
        const open_num = parseInt(operationHours?.open_time.split(':')[0])
        const close_num = parseInt(operationHours?.close_time.split(':')[0])
        if (req.day === shiftDate) {
            if (operationHours?.next_day_end) {
                return req.hour >= open_num - 1
            }
            return req.hour >= open_num - 1 && req.hour <= close_num + 1
        } else if (req.day === moment(shiftDate, 'YYYY-MM-DD').add(1, 'days').format('YYYY-MM-DD')) {
            return req.hour <= close_num + 1
        }
        return false
    })
    const full = filteredStaffReqs.reduce(
        (acc, { staff_required, customer_staff_required }) => acc + (customer_staff_required || staff_required),
        0
    )

    const newHoursWithStaffReq = {}
    filteredStaffReqs.forEach(staffReq => {
        if (staffReq.staff_required > 0 || staffReq.customer_staff_required > 0) {
            newHoursWithStaffReq[moment(staffReq.day, 'YYYY-MM-DD').hour(staffReq.hour).format('D-H')] =
                staffReq.customer_staff_required || staffReq.staff_required
        }
    })
    return [filteredStaffReqs, { ...newHoursWithStaffReq, fullCount: full }]
}
export const getShifts = (shifts = [], store, shiftRequests = []) => {
    const seenTimeBlocks = {}
    let rowNumber = 0
    const mappedShifts = shifts
        .filter(shift =>
            seenTimeBlocks[shift.time_block + shift.employment_id + shift.status]
                ? false
                : (seenTimeBlocks[shift.time_block + shift.employment_id + shift.status] = true)
        )
        .map(shift => {
            return {
                ...shift,
                startDate: parseTimeBlock(shift.time_block, timezoneOffsets[store.time_zone])[0],
                endDate: parseTimeBlock(shift.time_block, timezoneOffsets[store.time_zone])[1],
                title: '',
                employment_id: shift.employment_id
            }
        })
    shiftRequests?.forEach(shift => {
        rowNumber = Math.max(rowNumber, parseInt(shift.row_number))
        shift.employment_id = shift.employment_id || 'fusoku-' + shift.row_number
        shift.startDate = parseTimeBlock(shift.time_block, timezoneOffsets[store.time_zone])[0]
        shift.endDate = parseTimeBlock(shift.time_block, timezoneOffsets[store.time_zone])[1]
        shift.title = ''
        shift.status = 'confirmed'
        shift.isFusoku = true
    })
    return [mappedShifts, shiftRequests, rowNumber]
}

export const confirmedBlocksByEmployment = (blocks = []) => {
    const map = {}
    blocks.forEach(shift => {
        if (shift.status !== 'confirmed' || shift.isFusoku) return
        const id = String(shift.employment_id)
        if (!id || id.includes('fusoku')) return
        ;(map[id] ||= []).push(shift.startDate?.getTime?.() || 0)
    })
    Object.keys(map).forEach(id => map[id].sort((a, b) => a - b))
    return map
}

export const changedConfirmedEmploymentIds = (originalBlocks = [], currentBlocks = []) => {
    const original = confirmedBlocksByEmployment(originalBlocks)
    const current = confirmedBlocksByEmployment(currentBlocks)
    return [...new Set([...Object.keys(original), ...Object.keys(current)])].filter(
        id => (original[id] || []).join(',') !== (current[id] || []).join(',')
    )
}

const CheckShiftDay = ({ 
    history,
    setSnackbar,
    setShiftDate,
    shiftDate,
    setShowMonthlyView,
    setHasUnsavedChanges = () => {} }) => {
    const { employmentId } = useParams()
    const [originalShifts, setOriginalShifts] = useState([])
    const [employments, setEmployments] = useState([])
    const [staffRequirements, setStaffRequirements] = useState([])
    const [hoursWithStaffReq, setHoursWithStaffReq] = useState({})
    const { isManagerFor = {}, canEditShiftFor = {}, userInfo } = useContext(EmploymentsContext)
    const [shifts, setShifts] = useState([])
    const [nonConfirmedShifts, setNonConfirmedShifts] = useState([])
    const [confirmedShifts, setConfirmedShifts] = useState([])
    const [snackbarOpen, setSnackbarOpen] = useState(false)
    const [showAlert, setShowAlert] = useState(false)
    const [predictedSales, setPredictedSales] = useState(0)
    const [store, setStore] = useState()
    const [dialogue, setDialogue] = useState(false)
    const [startError, setStartError] = useState(false)
    const [endError, setEndError] = useState(false)
    const [endBeforeStartError, setEndBeforeStartError] = useState(false)
    const [endExceedsCloseError, setEndExceedsCloseError] = useState(false)
    const [durationExceedsLimitError, setDurationExceedsLimitError] = useState(false)
    const [startOutsideBusinessHoursError, setStartOutsideBusinessHoursError] = useState(false)
    const [modalEmployment, setModalEmployment] = useState()
    const [modalStartTime, setModalStartTime] = useState('12:00')
    const [modalEndTime, setModalEndTime] = useState('12:00')
    const [modalBreakStartTime, setModalBreakStartTime] = useState('')
    const [modalBreakEndTime, setModalBreakEndTime] = useState('')
    const [breakStartError, setBreakStartError] = useState(false)
    const [breakEndError, setBreakEndError] = useState(false)
    const [modalDate, setModalDate] = useState()
    const [desktop, setDesktop] = useState(false)
    const [originalData, setOriginalData] = useState()
    const [storeHour, setStoreHour] = useState()
    const [dailyNotes, setDailyNotes] = useState({})
    const [memoLoading, setMemoLoading] = useState(false)
    const [shiftSubmitLoading, setShiftSubmitLoading] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState(null)
    const [collapsedRows, setCollapsedRows] = useState(false)
    const [changed, setChanged] = useState(false)
    const [changeReasons, setChangeReasons] = useState({})
    const [fusokuNumber, setFusokuNumber] = useState(0)
    const [blockers, setBlockers] = useState([])
    const [isDataLoaded, setIsDataLoaded] = useState(false)
    const [editTemplate, setEditTemplate] = useState(false)
    const [dailyNotePopoverAnchorEl, setDailyNotePopoverAnchorEl] = useState(null)
    const [templates, setTemplates] = useState([])
    const [submitShiftDialog, setSubmitShiftDialog] = useState(false)
    const [suggestedStartEnd, setSuggestedStartEnd] = useState()
    const [openSupporterDrawer, setOpenSupporterDrawer] = useState(false)
    const [selectedRowNumber, setSelectedRowNumber] = useState()
    const [supporterEmployments, setSupporterEmployments] = useState([])
    const orientation = useOrientation()
    const [showNavigationPrompt, setShowNavigationPrompt] = useState(false)
    const [pendingNavigation, setPendingNavigation] = useState(null)
    const location = useLocation()
    const [reload, setReload] = useState(true)
    const [autoscheduleSnapshot, setAutoscheduleSnapshot] = useState(null)

    const dailyNotesPopoverOpen = Boolean(dailyNotePopoverAnchorEl)
    const popoverId = dailyNotesPopoverOpen ? 'simple-popover' : undefined
    useEffect(() => {
        if (store?.id) {
            setIsDataLoaded(true)
        }
    }, [store])

    // Notify parent component about unsaved changes
    useEffect(() => {
        if (setHasUnsavedChanges) {
            setHasUnsavedChanges(changed)
        }
    }, [changed, setHasUnsavedChanges])

    // Reload data when shiftDate changes externally (from parent component)
    useEffect(() => {
        if (shiftDate && employmentId) {
            setReload(true)
        }
    }, [shiftDate, employmentId])
    const handleTextareaChange = e => {
        const lines = e.target.value.split('\n')
        if (lines.length <= MEMO_MAX_ROWS) {
            // Limit rows
            setDailyNotes(prevNotes => ({
                ...prevNotes,
                note: e.target.value
            }))
        } else {
            setSnackbar({ message: 'メモは５行まで編集可能です', severity: 'error' })
            return
        }
    }

    const scrollToTop = () => {
        window.scrollTo(0, 0)
    }

    const moveToNextDay = () => {
        const nextDay = moment(shiftDate, 'YYYY-MM-DD').add(1, 'days')
        setShiftDate(nextDay.format('YYYY-MM-DD'))
        setReload(true)
        scrollToTop()
    }

    const moveToPreviousDay = () => {
        const previousDay = moment(shiftDate, 'YYYY-MM-DD').subtract(1, 'days')
        setShiftDate(previousDay.format('YYYY-MM-DD'))
        setReload(true)
        scrollToTop()
    }

    const getStaffRequired = hour => {
        const requirements = staffRequirements?.find(staffReq => staffReq.hour === hour)
        return [
            requirements?.customer_staff_required || requirements?.staff_required,
            requirements?.customer_preds || requirements?.pred_sales
        ]
    }

    const checkIfFullyStaffed = (day, hour, staffRequired) => {
        let shiftCount = 0
        shifts.forEach(shift => {
            if (
                shift.status === 'confirmed' &&
                shift.startDate.getHours() === hour &&
                shift.startDate.getDate() === day
            ) {
                shiftCount++
            }
        })
        return [shiftCount / 4 >= staffRequired, shiftCount / 4]
    }

    const staffCountForPredictedTimeSlots = () => {
        let shiftCount = 0
        const hourCounts = {}
        Object.keys(hoursWithStaffReq).forEach(hour => {
            hourCounts[hour] = 0
        })
        shifts.forEach(shift => {
            if (
                shift.status === 'confirmed' &&
                hoursWithStaffReq[shift.startDate.getDate() + '-' + shift.startDate.getHours()] &&
                hourCounts[shift.startDate.getDate() + '-' + shift.startDate.getHours()] <
                    hoursWithStaffReq[shift.startDate.getDate() + '-' + shift.startDate.getHours()]
            ) {
                hourCounts[shift.startDate.getDate() + '-' + shift.startDate.getHours()] += 0.25
                shiftCount += 0.25
            }
        })
        return shiftCount
    }
    const staffCountForAllTimeSlots = () => {
        let shiftCount = 0
        const hourCounts = {}
        if (!storeHour) return 0
        const startHour = parseInt(storeHour.open_time.split(':')[0])
        const endHour = parseInt(storeHour.close_time.split(':')[0]) + (storeHour.next_day_end ? 24 : 0)
        const date = moment(shiftDate, 'D')
        for (let i = startHour; i <= endHour; i++) {
            const key = date + '-' + i
            hourCounts[key] = 0
        }

        shifts.forEach(shift => {
            if (shift.status === 'confirmed') {
                const key = date + '-' + shift.startDate.getHours()
                hourCounts[key] += 0.25
                shiftCount += 0.25
            }
        })
        return shiftCount
    }

    const fullDayStaffed = () => {
        const staffCountForPrediction = staffCountForPredictedTimeSlots()
        const staffCountForAllTime = staffCountForAllTimeSlots()
        const insufficient =
            (isManagerFor[employmentId] || canEditShiftFor[employmentId] || userInfo?.view_only) &&
            staffCountForPrediction < hoursWithStaffReq?.fullCount
        const sufficient =
            (isManagerFor[employmentId] || canEditShiftFor[employmentId] || userInfo?.view_only) &&
            staffCountForPrediction === staffCountForAllTime

        return (
            <div style={{ display: 'flex', flexDirection: 'row' }}>
                <PeopleAlt
                    fontSize="string"
                    sx={{
                        color: insufficient ? ERROR_COLOR : sufficient ? 'inherit' : PALO_BLUE
                    }}
                />
                <Typography
                    style={{
                        marginLeft: 5,
                        fontSize: 12,
                        color: insufficient ? ERROR_COLOR : sufficient ? 'inherit' : PALO_BLUE
                    }}
                >
                    {staffCountForAllTime}/{hoursWithStaffReq.fullCount}
                </Typography>
            </div>
        )
    }

    const handleCalendarClick = (shift, employment={}) => {
        if (shift.row_number && isChecking) {
            const fusokuRow = employments.find(e => e.id === shift.employment_id)
            const fusokuShifts = shifts.filter(s => s.row_number === fusokuRow.row_number)
            const earliestStart = new Date(Math.min(...fusokuShifts.map(s => new Date(s.startDate))))
            const latestEnd = new Date(Math.max(...fusokuShifts.map(s => new Date(s.endDate))))
            const timeBlock = earliestStart.toISOString() + '..' + latestEnd.toISOString()
            const [startTime, endTime] = timeBlock.split('..').map(time => {
                const isoTime = time.replace(' ', 'T').replace(' UTC', '.000Z')
                return moment(isoTime).format('HH:mm');
            })
            setSuggestedStartEnd([startTime, endTime])
            setSubmitShiftDialog(true)
            return 
        }
        if (isChecking) return
        if (userInfo?.view_only) return
        if (shift.status === 'blocked') return
        setChanged(true)
        if (shift.status === 'confirmed') {
            if (shift.originalStatus === 'submitted') {
                shift.status = shift.originalStatus
            } else {
                shift.status = ''
            }
            setConfirmedShifts(
                confirmedShifts.filter(s => {
                    return s.id !== shift.id
                })
            )
        } else {
            shift.status = 'confirmed'
            employment && (shift.employment_id = employment.id, shift.isFusoku = employment.isFusoku, shift.row_number = employment.row_number)
            setConfirmedShifts(prev => {
                return [...prev.filter(s => s.id !== shift.id), shift]
            })
        }
    }

    const handleLongPress = shift => {
        if (isChecking || userInfo?.view_only || shift.status === 'blocked') return
        
        setModalEmployment(shift.employment_id)
        setModalDate(shift.startDate)
        setDialogue(true)
        if (shift.status) {
            stitchShifts(
                shifts.filter(
                    e => e.employment_id == shift.employment_id && ['confirmed', 'submitted'].includes(e.status)
                ),
                timezoneOffsets[store.time_zone],
                true
            ).forEach(s => {
                if (
                    s.start_time.getTime() <= shift.startDate.getTime() &&
                    s.end_time.getTime() >= shift.endDate.getTime()
                ) {
                    setModalStartTime(moment(s.start_time).format('HH:mm'))
                    setModalEndTime(moment(s.end_time).format('HH:mm'))
                }
            })
            return
        }
        setModalStartTime(moment(shift.startDate).format('HH:mm'))
        setModalEndTime(moment(shift.endDate).format('HH:mm'))
    }

    const handleModalConfirm = () => {
        const v = validateShiftModalTimes(modalStartTime, modalEndTime, storeHour, modalDate)
        if (v.invalid) {
            setSnackbar({
                message: `勤務時間またはシフトの長さが正しくありません。営業時間内に収め、1日あたり${MAX_LEGAL_SHIFT_DURATION_HOURS}時間以内で入力してください。`,
                severity: 'error'
            })
            return
        }

        const [shour, sminute] = modalStartTime.split(':')
        const [ehour, eminute] = modalEndTime.split(':')
        const [bshour, bsminute] = modalBreakStartTime.split(':') // break start hour
        const [behour, beminute] = modalBreakEndTime.split(':') // break end hour
        const openHour = storeHour ? parseInt(storeHour.open_time?.split(':')[0] || 0, 10) : 0
        const closeHour = storeHour ? parseInt(storeHour.close_time?.split(':')[0] || 0, 10) : 0
        const crossesMidnight = storeHour ? (storeHour.next_day_end || closeHour < openHour) : false
        const endIsNextDay = crossesMidnight && parseInt(ehour, 10) < openHour
        const isFusoku = employments.find(e => e.id === modalEmployment)?.support_staff || (modalEmployment && String(modalEmployment).includes('fusoku'))
        const shift = {
            id: modalStartTime + modalEndTime + modalEmployment,
            status: 'confirmed',
            employment_id: modalEmployment,
            isFusoku,
            row_number: isFusoku && employments.find(e => e.id === modalEmployment)?.row_number,
            startDate: moment(modalDate).set({ hour: shour, minute: sminute }).toDate(),
            endDate:
                endIsNextDay
                    ? moment(modalDate).set({ hour: ehour, minute: eminute }).add(1, 'day').toDate()
                    : moment(modalDate).set({ hour: ehour, minute: eminute }).toDate(),
            breakStartDate: behour < 5 ? moment(modalDate).set({ hour: bshour, minute: bsminute }).add(1, 'day').toDate() : moment(modalDate).set({ hour: bshour, minute: bsminute }).toDate(),
            breakEndDate: behour < 5 ? moment(modalDate).set({ hour: behour, minute: beminute }).add(1, 'day').toDate() : moment(modalDate).set({ hour: behour, minute: beminute }).toDate(),
            title: ''
        }
        
        const notOverlapped = confirmedShifts.filter(s => {
            return !(
                s.employment_id === modalEmployment &&
                s.startDate.getTime() >= shift.startDate.getTime() &&
                s.startDate.getTime() < shift.endDate.getTime()
            )
        })
        const split = splitShiftsIntoBlocks([shift])
        setChanged(true)
        setConfirmedShifts(() => [...notOverlapped, ...split])
        setDialogue(false)
    }

    const handleModalDelete = () => {
        const [shour, sminute] = modalStartTime.split(':')
        const [ehour, eminute] = modalEndTime.split(':')
        const modalStartDate = moment(modalDate).set({ hour: shour, minute: sminute }).toDate()
        const modalEndDate = moment(modalDate).set({ hour: ehour, minute: eminute }).toDate()

        const remainingShifts = confirmedShifts.filter(shift => {
            if (
                shift.employment_id != modalEmployment ||
                shift.startDate.getTime() < modalStartDate.getTime() ||
                shift.startDate.getTime() >= modalEndDate.getTime()
            ) {
                return true
            } else {
                if (shift.originalStatus === 'submitted') {
                    shift.status = shift.originalStatus
                } else {
                    shift.status = ''
                }
                return false
            }
        })
        setChanged(true)
        setConfirmedShifts(remainingShifts)
        setDialogue(false)
    }

    const handleShiftSubmit = async () => {
        setShiftSubmitLoading(true)
        try {
            setChanged(false)
            let finishedSubmitting = false
            const stiched = stitchShifts(confirmedShifts, timezoneOffsets[store.time_zone])

            const allShifts = stiched.filter(shift => !shift.isFusoku)
            const fusokuShifts = stiched.filter(shift => shift.isFusoku)
            if (fusokuShifts?.length) {
                await AxiosWithAuth.post('shift_requests/multiple', {
                    employment_id: employmentId,
                    date: shiftDate,
                    shifts: fusokuShifts.map(shift => ({
                        store_id: store.id,
                        start_time: shift.start_time,
                        end_time: shift.end_time,
                        row_number: shift.row_number,
                        ops_date: shiftDate,
                        employment_id: String(shift?.employment_id)?.includes('fusoku') ? null : shift.employment_id
                    }))
                }).then(res => {
                    finishedSubmitting = true
                    // create_multiple replaces all shift_requests for the date
                    setOriginalData(prev => ({
                        ...prev,
                        shift_requests: res.data
                    }))
                }).catch(err => {
                    finishedSubmitting = false
                    setSnackbar({ message: err.response?.data, severity: 'error' })
                })
            } else {
                const originalFusokuShiftsIds = originalData.shift_requests.map(shift => shift.id)
                await AxiosWithAuth.delete('/multi_shift_requests', {
                    data: {
                        employment_id: employmentId,
                        shift_request: {
                            ids: originalFusokuShiftsIds
                        }
                    }
                }).then(() => {
                    finishedSubmitting = true
                    setOriginalData(prev => ({
                        ...prev,
                        shift_requests: prev.shift_requests.filter(shift => !originalFusokuShiftsIds.includes(shift.id))
                    }))
                }).catch(() => {
                    finishedSubmitting = false
                    setSnackbar({ message: 'シフトの保存に失敗しました', severity: 'error' })
                })
            }
            const changeReasonsPayload = ENABLE_CONFIRMED_SHIFT_CHANGE_REASON
                ? Object.fromEntries(
                    changedEmploymentIds.filter(id => changeReasons[id]).map(id => [id, changeReasons[id]])
                )
                : {}
            if (allShifts?.length) {
                const confirmPayload = {
                    employment_id: employmentId,
                    shifts: allShifts,
                    ops_date: shiftDate
                }
                if (ENABLE_CONFIRMED_SHIFT_CHANGE_REASON && Object.keys(changeReasonsPayload).length > 0) {
                    confirmPayload.change_reasons = changeReasonsPayload
                }
                await AxiosWithAuth.post('/confirmShifts', confirmPayload)
                    .then(res => {
                        finishedSubmitting = true
                        setOriginalData(prev => ({
                            ...prev,
                            shifts: [...prev.shifts, ...res.data]
                        }))
                    })
                    .catch(() => {
                        finishedSubmitting = false
                        setSnackbar({ message: 'シフトの保存に失敗しました', severity: 'error' })
                    })
            } else {
                const originalShiftsIds = originalData.shifts
                    .filter(shift => shift.status === 'confirmed')
                    .map(shift => shift.id)
                const deletePayload = {
                    employment_id: employmentId,
                    shift: { ids: originalShiftsIds }
                }
                if (ENABLE_CONFIRMED_SHIFT_CHANGE_REASON && Object.keys(changeReasonsPayload).length > 0) {
                    deletePayload.change_reasons = changeReasonsPayload
                }
                await AxiosWithAuth.delete('/multishifts', {
                    data: deletePayload
                }).then(() => {
                    finishedSubmitting = true
                    setOriginalData(prev => ({
                        ...prev,
                        shifts: prev.shifts.filter(shift => !originalShiftsIds.includes(shift.id))
                    }))
                }).catch(() => {
                    finishedSubmitting = false
                    setSnackbar({ message: 'シフトの保存に失敗しました', severity: 'error' })
                })
            }
            if (finishedSubmitting) {
                removeUnloadHandler()
                setChangeReasons({})
                // Include 不足シフト (isFusoku) so the calendar reflects edits without a refresh
                setOriginalShifts(prev => [
                    ...(prev || []).filter(shift => shift.status !== 'confirmed'),
                    ...confirmedShifts.map(shift => ({ ...shift }))
                ])
                setSnackbarMessage(null)
                setSnackbarOpen(true)
            }
        } finally {
            setShiftSubmitLoading(false)
        }
    }

    moment.updateLocale('en', {
        weekdaysShort: ['日曜日', '月曜日', '火曜日', '水曜日', '木曜日', '金曜日', '土曜日']
    })

    const formatCurrentDate = () => {
        return moment(shiftDate, 'YYYY-MM-DD').format('YYYY年M月D日 ddd')
    }

    const generateEmploymentText = employment => {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    minHeight: '5.5rem',
                    maxHeight: '5.5rem'
                }}
                key={employment.id}
            >
                <Typography sx={{ textAlign: 'center' }} style={{ fontSize: 12 }}>
                    {displayNameWithSpace(employment.user)}
                </Typography>
                <Typography style={{ fontSize: 12 }}>{employment.user.emp_cd}</Typography>
                <Typography
                    style={{
                        fontSize: employment.open_close_duty ? 12 : 14
                    }}
                >
                    {`${employment.rank || ''}${employment.open_close_duty && employment.rank ? '/' : ''}`}
                    {employment.open_close_duty ? <VpnKeyRounded style={{ fontSize: 12 }} /> : ''}
                </Typography>
                <Typography style={{ fontSize: 12 }}>{ROLE_MAPPINGS[employment.role]}</Typography>
            </div>
        )
    }

    const handleData = data => {
        setChanged(false)
        setChangeReasons({})
        setOriginalData(JSON.parse(JSON.stringify(data))) // deep copy
        setDailyNotes({ ...data.note, original: data.note?.note })
        const [shifts, shiftRequests, rowNumber] = getShifts(data.shifts, data.store, data.shift_requests)

        const blockers = data.shift_blockers.map(blocker => ({
            ...blocker,
            status: 'blocked',
            title: '',
            startDate: parseTimeBlock(blocker.time_block, timezoneOffsets[data.store.time_zone])[0],
            endDate: parseTimeBlock(blocker.time_block, timezoneOffsets[data.store.time_zone])[1]
        }))
        setBlockers(blockers)
        let employments = data.employments
        setFusokuNumber(rowNumber)
        setSupporterEmployments(data.supporter_employments)
        const supporterList = {1: null, 2: null, 3: null, 4: null, 5: null}
        data.shift_requests.forEach(shift => {
            if (typeof shift.employment_id === 'number') {
                supporterList[shift.row_number] = shift.employment_id
            }
        })
        // Create a combined lookup map for O(1) employment lookups
        const employmentLookup = new Map()
        data.supporter_employments.forEach(emp => employmentLookup.set(String(emp.id), emp))
        data.employments.forEach(emp => employmentLookup.set(String(emp.id), emp))
        
        for (let i = 1; i <= rowNumber; i++) {
            // O(1) lookup using Map instead of O(n) + O(m) array searches
            const supporter = supporterList[i] ? employmentLookup.get(String(supporterList[i])) : null
            employments.push({
                ...supporter,
                employment_id: supporter ? supporter.id : 'fusoku-' + i,
                id: supporter ? supporter.id : 'fusoku-' + i,
                isFusoku: true,
                row_number: i,
                user: {
                    id: supporter ? supporter.id : null,
                    name_first: supporter ? '(サポート)' : `${i}`,
                    name_last: supporter ? supporter?.user?.name_last : '不足シフト'
                }
            })
        }
        employments = employments.map(employment => ({
            ...employment,
            text: generateEmploymentText(employment)
        }))

        const combined = [...shifts, ...shiftRequests]
        setOriginalShifts(combined)
        setShifts(combined)
        setEmployments(employments.sort((a, b) => {
            if (a.isFusoku && !b.isFusoku) return 1;
            if (!a.isFusoku && b.isFusoku) return -1;
            return a.order - b.order;
        }))
        const [filteredStaffReqs, hoursWithStaffReq] = getStaffReq(
            data.staff_requirements,
            data.operation_hours,
            shiftDate
        )
        setStaffRequirements(filteredStaffReqs)
        setHoursWithStaffReq(hoursWithStaffReq)
        setPredictedSales(data.customer_predicted_sales || data.predicted_sales)
        setStore(data.store)
        setStoreHour(data.operation_hours)
        if (!filteredStaffReqs.length) {
            setShowAlert(true)
        }
    }

    const resetShifts = () => {
        
        handleData(originalData)
    }

    const saveNotes = () => {
        setMemoLoading(true)
        if (dailyNotes?.id) {
            AxiosWithAuth.put(`/daily_notes/${dailyNotes?.id}`, {
                note: dailyNotes.note
            }).then(({ data }) => {
                setMemoLoading(false)
                setDailyNotes({ ...data, original: data.note })
                setSnackbarMessage('メモを保存しました')
                setSnackbarOpen(true)
            })
        } else {
            AxiosWithAuth.post('/daily_notes', {
                store_id: store.id,
                date: shiftDate,
                note: dailyNotes?.note
            }).then(({ data }) => {
                setMemoLoading(false)
                setDailyNotes({ ...data, original: data.note })
                setSnackbarMessage('メモを保存しました')
                setSnackbarOpen(true)
            })
        }
    }

    const removeUnloadHandler = () => {
        window.removeEventListener('beforeunload', onBeforeUnload, { capture: true })
    }

    const addFusokuUser = () => {
        if (fusokuNumber >= 5) {
            setSnackbar({ message: '不足シフトは５つまで追加できます', severity: 'error' })
            return
        }
        const newEmployment = {
            id: 'fusoku-' + (fusokuNumber + 1),
            emp_cd: fusokuNumber + 1,
            isFusoku: true,
            row_number: fusokuNumber + 1,
            user: {
                name_first: `${fusokuNumber + 1}`,
                name_last: '不足シフト'
            }
        }
        newEmployment.text = generateEmploymentText(newEmployment)
        setFusokuNumber(fusokuNumber + 1)
        setEmployments([...employments, newEmployment])
    }

    useEffect(() => {
        if (changed) {
            window.addEventListener('beforeunload', onBeforeUnload, { capture: true })
        } else {
            removeUnloadHandler()
        }
        return () => {
            removeUnloadHandler()
        }
    }, [changed])

    useEffect(() => {
        const v = validateShiftModalTimes(modalStartTime, modalEndTime, storeHour, modalDate)
        setEndBeforeStartError(v.endBeforeStart)
        setEndExceedsCloseError(v.endExceedsClose)
        setDurationExceedsLimitError(v.durationExceedsLimit)
        setStartOutsideBusinessHoursError(v.startOutsideBusinessHours)
        setStartError(v.startGridInvalid || v.startOutsideBusinessHours)
        setEndError(
            v.endMinutesInvalid || v.endBeforeStart || v.endExceedsClose || v.durationExceedsLimit
        )
        if (modalBreakStartTime.split(':')[1] % 15 > 0) {
            setBreakStartError(true)
        } else {
            setBreakStartError(false)
        }
        if (modalBreakEndTime.split(':')[1] % 15 > 0) {
            setBreakEndError(true)
        } else {
            setBreakEndError(false)
        }
    }, [modalStartTime, modalEndTime, modalBreakStartTime, modalBreakEndTime, storeHour, modalDate])

    useEffect(() => {
        if (originalShifts) {
            const splitShifts = splitShiftsIntoBlocks(originalShifts)
            const splitBlockers = splitShiftsIntoBlocks(blockers)
            setNonConfirmedShifts(splitShifts.filter(shift => shift.status !== 'confirmed').concat(splitBlockers))
            if (isManagerFor[employmentId] || canEditShiftFor[employmentId] || userInfo?.view_only) {
                setConfirmedShifts(splitShifts.filter(shift => shift.status === 'confirmed'))
            } else {
                setConfirmedShifts(splitShifts.filter(shift => shift.is_released || shift.row_number))
            }
        }
    }, [originalShifts, isManagerFor, canEditShiftFor, employmentId, userInfo])

    const originalConfirmedBlocks = useMemo(
        () => splitShiftsIntoBlocks((originalShifts || []).filter(shift => shift.status === 'confirmed' && !shift.isFusoku)),
        [originalShifts]
    )
    const changedEmploymentIds = useMemo(
        () => changedConfirmedEmploymentIds(originalConfirmedBlocks, confirmedShifts),
        [originalConfirmedBlocks, confirmedShifts]
    )
    const setEmploymentChangeReason = (employmentIdKey, reason) => {
        setChangeReasons(prev => ({ ...prev, [String(employmentIdKey)]: reason }))
    }

    useEffect(() => {
        const confirmedShiftsToExclude = []
        let combinedShifts = nonConfirmedShifts.map(shift => {
            let overlappingShift = null
            confirmedShifts.forEach(confirmedShift => {
                if (
                    confirmedShift?.startDate.getTime() === shift.startDate.getTime() &&
                    confirmedShift?.employment_id === shift.employment_id
                ) {
                    overlappingShift = confirmedShift
                    confirmedShiftsToExclude.push(confirmedShift.id)
                }
            })
            return overlappingShift || shift
        })
        combinedShifts = combinedShifts.concat(
            confirmedShifts
                .filter(shift => !confirmedShiftsToExclude.includes(shift.id))
                .map(shift => ({ ...shift, notAvailable: true }))
        )
        setShifts(combinedShifts)
    }, [confirmedShifts, nonConfirmedShifts])

    useEffect(() => {
        if (shiftDate && employmentId && reload) {
            AxiosWithAuth.get('/calendars/dailyShifts', {
                params: { employment_id: employmentId, date: shiftDate }
            }).then(res => {
                handleData(res.data)
                setReload(false)
            })
        }
    }, [shiftDate, employmentId, reload])
    useEffect(() => {
        if (isManagerFor[employmentId] || canEditShiftFor[employmentId]) {
            AxiosWithAuth.get('/daily_note_templates', {
                params: { employment_id: employmentId }
            })
                .then(res => {
                    setTemplates(res.data)
                })
                .catch(err => {
                    console.log(err)
                })
        }
    }, [isManagerFor, canEditShiftFor, employmentId])

    useEffect(() => {
        if (!ENABLE_AUTOSCHEDULE_DAY_QUALITY) {
            setAutoscheduleSnapshot(null)
            return
        }
        if (!store?.id || !shiftDate) {
            setAutoscheduleSnapshot(null)
            return
        }
        if (!(isManagerFor[employmentId] || canEditShiftFor[employmentId])) return

        AxiosWithAuth.get('/autoschedule_snapshots/latest', {
            params: { store_id: store.id, ops_date: shiftDate }
        })
            .then(res => setAutoscheduleSnapshot((res.data || [])[0] || null))
            .catch(() => setAutoscheduleSnapshot(null))
    }, [store?.id, shiftDate, employmentId, isManagerFor, canEditShiftFor])

    const handleQualityTag = (id, tag) => {
        AxiosWithAuth.put(`/autoschedule_snapshots/${id}`, { tag })
            .then(res => setAutoscheduleSnapshot(res.data))
            .catch(() => {
                setSnackbarMessage('評価の保存に失敗しました')
                setSnackbarOpen(true)
            })
    }

    const canTagAutoschedule =
        (isManagerFor[employmentId] || canEditShiftFor[employmentId]) && !userInfo?.view_only

    useEffect(() => {
        if (isMobile()) {
            setDesktop(false)
        } else {
            setDesktop(true)
        }
    }, [])

    const isChecking = history.location.pathname.includes('check')

    const timeChunks = useMemo(() => {
        let open = 7
        let close = 27

        if (storeHour?.close_time && storeHour?.open_time) {
            const closing = storeHour?.close_time.split(':')
            if (parseInt(closing[1]) > 0) {
                close = parseInt(closing[0]) + 1
            } else {
                close = parseInt(closing[0])
            }
            open = parseInt(storeHour?.open_time.split(':')[0])
            if (close < open || storeHour.next_day_end) {
                close += 24
            }
        } else if (staffRequirements.length > 0) {
            const sortedRequirements = staffRequirements
                .filter(req => req['pred_sales'] > 0)
                .sort((a, b) => a.hour - b.hour)
            open = sortedRequirements[0]?.hour
            close = sortedRequirements.at(-1)?.hour + 1
        }

        let chunks = []
        const [year, month, day] = shiftDate.split('-')
        const shiftDay = new moment([year, month - 1, day])
        for (let i = open * 4 * 15; i < close * 60; i += 15) {
            const time = shiftDay.toDate()
            time.setHours(0)
            time.setMinutes(i)
            chunks.push(time)
        }
        return chunks
    }, [shiftDate, staffRequirements, storeHour])

    /** 予定時間合計が法定上限を超える行（不足シフト行は対象外） */
    const employmentShiftHoursOverLimit = useMemo(() => {
        const set = new Set()
        employments.forEach(emp => {
            if (emp.isFusoku) return
            let total = 0
            shifts.forEach(shift => {
                if (
                    String(shift.employment_id) === String(emp.id) &&
                    shift.status === 'confirmed' &&
                    !shift.isFusoku &&
                    !String(shift.employment_id).includes('fusoku')
                ) {
                    total += SHIFT_CALENDAR_SLOT_HOURS
                }
            })
            if (total > MAX_LEGAL_SHIFT_DURATION_HOURS) set.add(String(emp.id))
        })
        return set
    }, [shifts, employments])

    const hasShiftHoursOverLimit = employmentShiftHoursOverLimit.size > 0

    const Legend = () => {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginRight: desktop ? '1%' : '',
                    width: desktop ? '30%' : ''
                }}
            >
                <div className="legend-key">
                    <div
                        style={{
                            background: 'repeating-linear-gradient(to right, #BAC0E4, #BAC0E4 3px, #fff 3px, #fff 6px)',
                            width: '15px',
                            height: '4px',
                            marginRight: '3px'
                        }}
                    ></div>
                    <Typography sx={{ ...buttonStyle, fontSize: 12, ml: 1 }}>希望時間</Typography>
                </div>
                <div className="legend-key">
                    <Circle fontSize="8px" color="confirmed" />
                    <Typography sx={{ ...buttonStyle, fontSize: 12, ml: 1 }}>シフト確定</Typography>
                </div>
                <div className="legend-key">
                    <Circle fontSize="8px" color="blocked" />
                    <Typography sx={{ ...buttonStyle, fontSize: 12, ml: 1 }}>他店ブロック</Typography>
                </div>
            </div>
        )
    }

    const NavigationButtons = () => {
        return (
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                style={{ width: desktop ? '15%' : '10%' }}
            >
                <IconButton 
                    onClick={() => {
                        if (changed) {
                            setPendingNavigation({ method: moveToPreviousDay })
                            setShowNavigationPrompt(true)
                        } else {
                            moveToPreviousDay()
                        }
                    }} 
                    sx={{ p: 0 }}
                >
                    <KeyboardArrowLeft />
                </IconButton>
                <IconButton 
                    onClick={() => {
                        if (changed) {
                            setPendingNavigation({ method: () => {setShowMonthlyView(true); setShiftDate('')}})
                            setShowNavigationPrompt(true)
                        } else {
                            setShowMonthlyView(true)
                            setShiftDate('')
                        }
                    }} 
                    sx={{ p: 0 }}
                >
                    <CalendarToday />
                </IconButton>
                <IconButton 
                    onClick={() => {
                        if (changed) {
                            setPendingNavigation({ method: moveToNextDay })
                            setShowNavigationPrompt(true)
                        } else {
                            moveToNextDay()
                        }
                    }} 
                    sx={{ p: 0 }}
                >
                    <KeyboardArrowRight />
                </IconButton>
            </Stack>
        )
    }

    const ExportButtons = () => {
        return (
            <>
                {isDataLoaded && <PdfBulkExportButton shiftDates={[shiftDate]} />}
            </>
        )
    }

    const onBeforeUnload = e => {
        if (changed) {
            e.preventDefault()
            e.returnValue = ''
            return ''
        }
    }

    const handleNavigationAttempt = (nextLocation) => {
        if (changed) {
            // Only allow navigation if it's exactly the same pathname (no tab changes or other modifications)
            if (nextLocation.pathname === location.pathname) {
                return true
            }
            setPendingNavigation({ pathname: nextLocation.pathname })
            setShowNavigationPrompt(true)
            return false
        }
        return true
    }

    const handleNavigationConfirm = () => {
        setShowNavigationPrompt(false)
        // Reset shifts to original data before navigating away
        if (originalData) {
            handleData(originalData)
        }
        setChanged(false)
        
        if (pendingNavigation) {
            if (pendingNavigation.method) {
                pendingNavigation.method()
                setPendingNavigation(null)
            } else if (pendingNavigation.pathname) {
                // Temporarily unblock history to allow navigation
                if (window.currentHistoryUnblock) {
                    window.currentHistoryUnblock()
                }
                history.push(pendingNavigation.pathname)
                setPendingNavigation(null)
            } 
            else if (pendingNavigation.action === 'POP') {
                history.goBack()
                setPendingNavigation(null)
            }
        }
    }

    const handleNavigationCancel = () => {
        setShowNavigationPrompt(false)
        setPendingNavigation(null)
    }

    // Handle browser back/forward buttons and gestures
    useEffect(() => {
        const handlePopState = (event) => {
            if (changed) {
                event.preventDefault()
                setPendingNavigation({ action: 'POP' })
                setShowNavigationPrompt(true)
            }
        }

        if (changed) {
            window.addEventListener('popstate', handlePopState)
            return () => {
                window.removeEventListener('popstate', handlePopState)
            }
        }
    }, [changed, history, location])

    useEffect(() => {
        if (changed) {
            window.addEventListener('beforeunload', onBeforeUnload, { capture: true })
            const unblock = history.block(handleNavigationAttempt)
            window.currentHistoryUnblock = unblock

            return () => {
                window.removeEventListener('beforeunload', onBeforeUnload, { capture: true })
                unblock()
                window.currentHistoryUnblock = null
            }
        }
    }, [changed, history, location])

    const buttonStyle = {
        maxHeight: '40px',
        whiteSpace: 'nowrap',
        minWidth: 'fit-content',
        flexWrap: 'nowrap'
    }
    return (
        <>
            <Dialog
                open={showNavigationPrompt}
                onClose={handleNavigationCancel}
                aria-labelledby="navigation-prompt-title"
            >
                <DialogTitle id="navigation-prompt-title">
                    変更が保存されていません
                </DialogTitle>
                <DialogContent>
                    ページを切り替えると行った変更が保存されない可能性があります。よろしいですか。
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleNavigationCancel} color="primary">
                        キャンセル
                    </Button>
                    <Button onClick={handleNavigationConfirm} color="primary" autoFocus>
                        移動する
                    </Button>
                </DialogActions>
            </Dialog>
            <div>
                <Dialog open={dialogue} data-testid="modal">
                    <Box display="flex" direction="row" justifyContent="space-between" alignItems={'center'}>
                        <DialogTitle>勤務時間</DialogTitle>
                        <DeleteOutline data-testid="modalDelete" onClick={handleModalDelete} sx={{ mr: '1rem' }} />
                    </Box>
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', width: '20rem' }}>
                        <Stack spacing={1.5} sx={{ display: 'flex', flexDirection: 'column' }}>
                            <InputTime
                                startValue={modalStartTime}
                                endValue={modalEndTime}
                                startOnChange={e => setModalStartTime(e.target.value)}
                                endOnChange={e => setModalEndTime(e.target.value)}
                                startError={startError}
                                endError={endError}
                                startHelperText={
                                    startError
                                        ? Number(modalStartTime.split(':')[1]) % 15 > 0
                                            ? '15分単位でのスケジュールが可能です。'
                                            : startOutsideBusinessHoursError
                                              ? '開始時間は営業時間内に収めてください。'
                                              : '15分単位でのスケジュールが可能です。'
                                        : undefined
                                }
                                endHelperText={
                                    endError
                                        ? endBeforeStartError
                                            ? '開始時間より後の終了時間を入力してください。'
                                            : endExceedsCloseError
                                              ? '終了時間は営業時間内に収めてください。'
                                              : durationExceedsLimitError
                                                ? `シフト時間が1日${MAX_LEGAL_SHIFT_DURATION_HOURS}時間を超えています。`
                                                : '15分単位でのスケジュールが可能です。'
                                        : undefined
                                }
                                startTestId={modalStartTime}
                                endTestId={modalEndTime}
                                addBreak={true}
                                breakStartTime={modalBreakStartTime}
                                breakEndTime={modalBreakEndTime}
                                setBreakStartTime={e => setModalBreakStartTime(e.target.value)}
                                setBreakEndTime={e => setModalBreakEndTime(e.target.value)}
                                breakStartError={breakStartError}
                                breakEndError={breakEndError}
                            />
                        </Stack>
                        {ENABLE_CONFIRMED_SHIFT_CHANGE_REASON && !isChecking && modalEmployment && !String(modalEmployment).includes('fusoku') && (
                            <ShiftChangeReasonSelect
                                value={changeReasons[String(modalEmployment)] || ''}
                                onChange={reason => setEmploymentChangeReason(modalEmployment, reason)}
                                labelId="modal-change-reason-label"
                                testId={`change-reason-${modalEmployment}`}
                                sx={{ mt: 2 }}
                            />
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button autoFocus onClick={() => setDialogue(false)}>
                            キャンセル
                        </Button>
                        <Button
                            disabled={startError || endError || breakStartError || breakEndError}
                            onClick={() => handleModalConfirm()}
                            autoFocus
                        >
                            確定する
                        </Button>
                    </DialogActions>
                </Dialog>
                <div style={{ margin: desktop ? '1% 20%' : '2%' }}>
                    {userInfo?.view_only ? (
                        <TextField
                            size="small"
                            sx={{ border: 'none', mx: 'auto', width: desktop ? '35vw' : '95vw', display: 'block' }}
                            fullWidth
                            disabled
                            value={
                                JSON.parse(localStorage.selectedStoreViewOnly || '')?.seg_code +
                                ' - ' +
                                JSON.parse(localStorage.selectedStoreViewOnly || '')?.name
                            }
                        />
                    ) : null}
                </div>
                {showAlert && (
                    <Alert
                        sx={{ width: '80%', margin: '10px auto' }}
                        severity="warning"
                        onClose={() => setShowAlert(false)}
                    >
                        予測データ準備中・・・
                    </Alert>
                )}
                {desktop && (
                    <>
                        <Stack direction={'row'} justifyContent="space-between" sx={{ m: '1rem' }} flexWrap='wrap'>
                            <Box display='flex' alignItems='center'>
                                <Typography variant="h6" sx={{...buttonStyle, marginRight: '1rem'}}>
                                    {formatCurrentDate()}
                                </Typography>
                                <NavigationButtons />                        
                            </Box>
                            <Box display='flex' alignItems='center' justifyContent='flex-end' flexWrap='wrap'>
                                {(isManagerFor[employmentId] ||
                                    canEditShiftFor[employmentId] ||
                                    userInfo?.view_only) && <ExportButtons />}
                                {!collapsedRows ? <Button
                                        variant="text"
                                        startIcon={<CompressOutlined />}
                                        onClick={() => setCollapsedRows(true)}
                                        sx={{...buttonStyle, mx: '.5rem' }}
                                    >
                                        行を圧縮
                                    </Button>
                                    :
                                    <Button
                                        variant="text"
                                        startIcon={<ExpandOutlined />}
                                        onClick={() => setCollapsedRows(false)}
                                        sx={{...buttonStyle, mx: '.5rem' }}
                                    >
                                        行を展開
                                    </Button>
                                }
                                {(canEditShiftFor[employmentId] || isManagerFor[employmentId]) &&
                                    (!isChecking) &&
                                    fusokuNumber < 5 && (
                                        <Button
                                            onClick={addFusokuUser}
                                            variant="text"
                                            startIcon={<AddOutlined />}
                                            sx={buttonStyle}
                                        >
                                            {'不足シフトの追加'}
                                        </Button>
                                    )}
                                {(canEditShiftFor[employmentId] || isManagerFor[employmentId]) && !isChecking && (
                                    <>
                                        {!isMobile() && (
                                            <Button
                                                variant="text"
                                                startIcon={<AutorenewOutlined />}
                                                onClick={resetShifts}
                                                sx={buttonStyle}
                                            >
                                                リセット
                                            </Button>
                                        )}
                                        <LoadingButton
                                            onClick={handleShiftSubmit}
                                            variant="contained"
                                            color="paloBlue"
                                            sx={buttonStyle}
                                            loading={shiftSubmitLoading}
                                            disabled={hasShiftHoursOverLimit}
                                        >
                                            シフトを保存する
                                        </LoadingButton>
                                    </>
                                )}
                            </Box>
                        </Stack>
                        <Stack direction={'column'} spacing={1} alignItems="center">
                            <div
                                style={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <TextareaAutosize
                                    className="MuiOutlinedInput"
                                    minRows={5}
                                    maxRows={5}
                                    placeholder="メモ"
                                    style={{
                                        width: '97%',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        resize: 'none'
                                    }}
                                    value={dailyNotes?.note || ''}
                                    onChange={e => {
                                        handleTextareaChange(e)
                                    }}
                                    disabled={!(isManagerFor[employmentId] || canEditShiftFor[employmentId])}
                                />
                                {(isManagerFor[employmentId] || canEditShiftFor[employmentId]) && (
                                    <div style={{ margin: '0.3rem', marginLeft: '-9rem', height: '2.5rem' }}>
                                        <IconButton
                                            aria-describedby={popoverId}
                                            onClick={e => setDailyNotePopoverAnchorEl(e.currentTarget)}
                                        >
                                            <AddCommentOutlined />
                                        </IconButton>
                                        <Popover
                                            open={dailyNotesPopoverOpen}
                                            anchorEl={dailyNotePopoverAnchorEl}
                                            onClose={() => setDailyNotePopoverAnchorEl(null)}
                                            anchorOrigin={{
                                                vertical: 'bottom',
                                                horizontal: 'center'
                                            }}
                                            transformOrigin={{
                                                vertical: 'top',
                                                horizontal: 'center'
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    flexDirection: 'column',
                                                    p: 1,
                                                    m: 1,
                                                    bgcolor: 'background.paper',
                                                    borderRadius: 1
                                                }}
                                            >
                                                {templates?.map(template => (
                                                    <Button
                                                        key={template.id}
                                                        variant="text"
                                                        style={{ color: 'black' }}
                                                        onClick={() => {
                                                            setDailyNotes({
                                                                ...dailyNotes,
                                                                note: (dailyNotes.note || '') + template.note + '\n'
                                                            })
                                                        }}
                                                    >
                                                        {template.note}
                                                    </Button>
                                                ))}
                                                <Button
                                                    variant="text"
                                                    onClick={() => {
                                                        setEditTemplate(true)
                                                        setDailyNotePopoverAnchorEl(null)
                                                    }}
                                                >
                                                    テンプレートを編集
                                                </Button>
                                            </Box>
                                        </Popover>
                                        <LoadingButton
                                            onClick={saveNotes}
                                            variant="contained"
                                            color="paloBlue"
                                            loading={memoLoading}
                                            disabled={dailyNotes?.note === dailyNotes?.original}
                                        >
                                            保存する
                                        </LoadingButton>
                                    </div>
                                )}
                            </div>
                        </Stack>
                        <Stack direction={'row'} justifyContent="flex-end">
                            <Legend />
                        </Stack>
                    </>
                )}
                {!desktop && (
                    <>
                        <Stack sx={{ my: '.5rem' }}>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}
                            >
                                <Typography variant="h8" sx={{ marginLeft: '0.5rem' }}>
                                    {formatCurrentDate()}
                                </Typography>
                                <NavigationButtons />
                                <div>
                                    {(isManagerFor[employmentId] ||
                                        canEditShiftFor[employmentId] ||
                                        userInfo?.view_only) &&
                                        isChecking && <ExportButtons />}

                                    {(canEditShiftFor[employmentId] || isManagerFor[employmentId]) && !isChecking && fusokuNumber < 5 && (
                                            <IconButton onClick={addFusokuUser}>
                                                <PersonAddAlt1 />
                                            </IconButton>
                                    )}
                                     {(canEditShiftFor[employmentId] || isManagerFor[employmentId]) && !isChecking &&
                                        <IconButton onClick={resetShifts}>
                                            <AutorenewOutlined />
                                        </IconButton>
                                    }
                                    <ButtonWithConfirmPrompt
                                        component={
                                            <IconButton>
                                                <ChatBubbleOutline />
                                            </IconButton>
                                        }
                                        title="メモを入力/確認する"
                                        content={
                                            <TextareaAutosize
                                                className="MuiOutlinedInput"
                                                minRows={5}
                                                maxRows={5}
                                                placeholder="メモ"
                                                style={{
                                                    width: '95%',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '4px',
                                                    resize: 'none'
                                                }}
                                                value={dailyNotes?.note || ''}
                                                onChange={e => {
                                                    handleTextareaChange(e)
                                                }}
                                                disabled={
                                                    !(isManagerFor[employmentId] || canEditShiftFor[employmentId])
                                                }
                                            />
                                        }
                                        confirmTextDisabled={
                                            !(isManagerFor[employmentId] || canEditShiftFor[employmentId]) ||
                                            dailyNotes?.note === dailyNotes?.original
                                        }
                                        confirmText="変更する"
                                        handleConfirm={() => {
                                            saveNotes()
                                        }}
                                    />
                                </div>
                            </div>
                        </Stack>
                        <Divider />
                        <Legend />
                        <Divider />
                    </>
                )}
                <AutoscheduleDayQuality
                    snapshot={autoscheduleSnapshot}
                    onTag={handleQualityTag}
                    canTag={canTagAutoschedule}
                />
                { orientation === "landscape" || (desktop && timeChunks) ? (
                    <DesktopCalendar
                        timeChunks={timeChunks}
                        fullDayStaffed={fullDayStaffed}
                        checkIfFullyStaffed={checkIfFullyStaffed}
                        getStaffRequired={getStaffRequired}
                        staffRequirements={staffRequirements}
                        predictedSales={predictedSales}
                        employments={employments}
                        shifts={shifts.concat(blockers)}
                        nonConfirmedShifts={nonConfirmedShifts}
                        computeColor={computeColor}
                        isManagerFor={isManagerFor}
                        canEditShiftFor={canEditShiftFor}
                        employmentId={employmentId}
                        handleCalendarClick={handleCalendarClick}
                        handleLongPress={handleLongPress}
                        hours={storeHour}
                        collapsedRows={collapsedRows}
                        viewOnly={userInfo?.view_only}
                        setOpenSupporterDrawer={setOpenSupporterDrawer}
                        setSelectedRowNumber={setSelectedRowNumber}
                        employmentShiftHoursOverLimit={employmentShiftHoursOverLimit}
                        changedEmploymentIds={changedEmploymentIds}
                        changeReasons={changeReasons}
                        onChangeReason={setEmploymentChangeReason}
                        showChangeReasons={ENABLE_CONFIRMED_SHIFT_CHANGE_REASON && !isChecking}
                    />
                ) : (
                    <MobileCalendar
                        timeChunks={timeChunks}
                        fullDayStaffed={fullDayStaffed}
                        checkIfFullyStaffed={checkIfFullyStaffed}
                        getStaffRequired={getStaffRequired}
                        staffRequirements={staffRequirements}
                        predictedSales={predictedSales}
                        employments={employments}
                        shifts={shifts.concat(blockers)}
                        nonConfirmedShifts={nonConfirmedShifts}
                        computeColor={computeColor}
                        isManagerFor={isManagerFor}
                        userInfo={userInfo}
                        canEditShiftFor={canEditShiftFor}
                        employmentId={employmentId}
                        handleCalendarClick={handleCalendarClick}
                        handleLongPress={handleLongPress}
                        isChecking={isChecking}
                        setOpenSupporterDrawer={setOpenSupporterDrawer}
                        setSelectedRowNumber={setSelectedRowNumber}
                        viewOnly={userInfo?.view_only}
                        employmentShiftHoursOverLimit={employmentShiftHoursOverLimit}
                        changedEmploymentIds={changedEmploymentIds}
                        changeReasons={changeReasons}
                        onChangeReason={setEmploymentChangeReason}
                        showChangeReasons={ENABLE_CONFIRMED_SHIFT_CHANGE_REASON && !isChecking}
                    />
                )}
                {!desktop && (canEditShiftFor[employmentId] || isManagerFor[employmentId]) && !isChecking && (
                    <Box sx={{ width: '100vw', position: 'fixed', bottom: '30px', zIndex: 1000, px: 2 }}>
                        <LoadingButton
                            onClick={handleShiftSubmit}
                            variant="contained"
                            color="paloBlue"
                            style={{ width: '-webkit-fill-available', margin: '1rem 1rem 2rem 1rem' }}
                            loading={shiftSubmitLoading}
                            disabled={hasShiftHoursOverLimit}
                        >
                            シフトを保存する
                        </LoadingButton>
                    </Box>
                )}
                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={4000}
                    onClose={() => {
                        setSnackbarMessage(null)
                        setSnackbarOpen(false)
                    }}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert
                        onClose={() => {
                            setSnackbarOpen(false)
                            setSnackbarMessage(null)
                        }}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    >
                        {snackbarMessage || 'シフトを保存しました。'}
                    </Alert>
                </Snackbar>
            </div>
            <EditDailyNoteTemplateDrawer
                setSnackbarMessage={setSnackbarMessage}
                setSnackbarOpen={setSnackbarOpen}
                editTemplate={editTemplate}
                setEditTemplate={setEditTemplate}
                templates={templates}
                setTemplates={setTemplates}
            />
            {openSupporterDrawer && (
                <SupporterDrawer
                    open={openSupporterDrawer}
                    setOpen={setOpenSupporterDrawer}
                    selectedRowNumber={selectedRowNumber}
                    setSelectedRowNumber={setSelectedRowNumber}
                    shiftDate={shiftDate}
                    setEmployments={setEmployments}
                    employments={employments}
                    generateEmploymentText={generateEmploymentText}
                    setConfirmedShifts={setConfirmedShifts}
                    confirmedShifts={confirmedShifts}
                    supporterEmployments={supporterEmployments}
                />
            )}
            {submitShiftDialog && (
                <Dialog open={submitShiftDialog}>
                    <DialogContent>
                        <SubmitShift
                            shiftDate={moment(shiftDate).format('YYYY-MM-DD')}
                            submitFor={employmentId}
                            setSubmitShiftDialog={setSubmitShiftDialog}
                            setAllShifts={() => null}
                            store={store}
                            setSnackbar={setSnackbar}
                            suggestedStartEnd={suggestedStartEnd}
                            setReload={setReload}
                        />
                    </DialogContent>
                </Dialog>
            )}
        </>
    )
}

export const computeColor = (authorized, shift) => {
    const color =
        !authorized && !shift.is_released
            ? null
            : shift.status === 'confirmed' && shift?.isFusoku
            ? FUSOKU
            : shift.status === 'confirmed'
            ? CONFIRMED
            : shift.status === 'submitted'
            ? '#fff'
            : shift.status === 'blocked'
            ? BLOCKED
            : null
    return color
}

export default CheckShiftDay
